import type {
  ConditionGroup,
  ConditionRule,
  ConditionValueType,
  OrderAutoMapRow
} from '@/types/master/orderAutoMap';
import type { NumberData } from '@/types/common';
import { getBusinessTypeLabel } from '@/libs/businessType';
import { ignitionType as ignitionTypeLabels } from '@/libs/harness';
import { mccsDeviceType } from '@/libs/device';

export type ConditionDisplayContext = {
  businessTypeLabels?: NumberData | null;
};

export function createEmptyConditionRule(): ConditionRule {
  return {
    fieldName: '',
    type: 'string',
    condition: 'equal',
    value: ''
  };
}

export function createDefaultConditionGroup(): ConditionGroup {
  return { rules: [createEmptyConditionRule()] };
}

export const conditionTypeOptions: Array<{
  label: string;
  value: ConditionValueType;
}> = [
  { label: '文字', value: 'string' },
  { label: '数値', value: 'number' },
  { label: '真偽値', value: 'boolean' }
];

export const conditionOperatorOptions: Record<
  ConditionValueType,
  Array<{ label: string; value: string }>
> = {
  string: [
    { label: '等しい', value: 'equal' },
    { label: '等しくない', value: 'not_equal' },
    { label: '含む', value: 'contains' },
    { label: '含まない', value: 'not_contains' },
    { label: 'で始まる', value: 'starts_with' },
    { label: 'で終わる', value: 'ends_with' }
  ],
  number: [
    { label: '等しい', value: 'equal' },
    { label: '等しくない', value: 'not_equal' },
    { label: 'より大きい', value: 'greater' },
    { label: '以上', value: 'greater_equal' },
    { label: 'より小さい', value: 'less' },
    { label: '以下', value: 'less_equal' }
  ],
  boolean: [
    { label: '等しい', value: 'equal' },
    { label: '等しくない', value: 'not_equal' }
  ]
};

export const booleanValueOptions = [
  { label: 'true', value: 'true' },
  { label: 'false', value: 'false' }
];

export const harnessCutValueOptions = [
  { label: 'ハーネスカットあり', value: 'true' },
  { label: 'ハーネスカットなし', value: 'false' }
];

export const HARNESS_CUT_FIELD = 'isHarnessCut';
export const HARNESS_CUT_FIELD_LABEL = 'ハーネスカット';
export const IGNITION_TYPE_FIELD = 'ignitionType';
export const IGNITION_TYPE_FIELD_LABEL = 'エンジンスタート方式';
export const SERVICE_CODE_FIELD = 'serviceCode';
export const BUSINESS_TYPE_FIELD = 'businessType';
export const BUSINESS_TYPE_FIELD_LABEL = '事業区分';
export const MOUNTING_PRICE_FIELD = 'mountingPrice';
export const MOUNTING_PRICE_FIELD_LABEL = '取付費用';
export const REMOVAL_PRICE_FIELD = 'removalPrice';
export const REMOVAL_PRICE_FIELD_LABEL = '取り外し費用';

const FIELD_DISPLAY_LABELS: Record<string, string> = {
  [HARNESS_CUT_FIELD]: HARNESS_CUT_FIELD_LABEL,
  [IGNITION_TYPE_FIELD]: IGNITION_TYPE_FIELD_LABEL,
  [SERVICE_CODE_FIELD]: 'サービスコード',
  [BUSINESS_TYPE_FIELD]: BUSINESS_TYPE_FIELD_LABEL,
  [MOUNTING_PRICE_FIELD]: MOUNTING_PRICE_FIELD_LABEL,
  [REMOVAL_PRICE_FIELD]: REMOVAL_PRICE_FIELD_LABEL
};

export function getFieldDisplayLabel(fieldName: string) {
  return FIELD_DISPLAY_LABELS[fieldName] ?? fieldName;
}

export const serviceCodeValueOptions = [
  { label: 'BQ', value: 'BQ' },
  { label: 'TP', value: 'TP' }
];

export function buildBusinessTypeValueOptions(
  businessTypes: Array<{ id: number; label: string }>
) {
  return businessTypes.map(({ id, label }) => ({
    label,
    value: String(id)
  }));
}

export const ignitionTypeValueOptions = Object.entries(ignitionTypeLabels).map(
  ([value, label]) => ({ label, value: String(value) })
);

export function isHarnessCutRule(rule: ConditionRule) {
  return rule.fieldName === HARNESS_CUT_FIELD;
}

export function isIgnitionTypeRule(rule: ConditionRule) {
  return rule.fieldName === IGNITION_TYPE_FIELD;
}

export function isServiceCodeRule(rule: ConditionRule) {
  return rule.fieldName === SERVICE_CODE_FIELD;
}

export function isBusinessTypeRule(rule: ConditionRule) {
  return rule.fieldName === BUSINESS_TYPE_FIELD;
}

export function formatConditionValue(
  rule: ConditionRule,
  context?: ConditionDisplayContext
) {
  if (isHarnessCutRule(rule) && rule.type === 'boolean') {
    const label = harnessCutValueOptions.find(
      (opt) => opt.value === rule.value
    )?.label;
    return label ?? rule.value;
  }
  if (isIgnitionTypeRule(rule)) {
    const label = ignitionTypeValueOptions.find(
      (opt) => opt.value === rule.value
    )?.label;
    return label ?? ignitionTypeLabels[Number(rule.value)] ?? rule.value;
  }
  if (isServiceCodeRule(rule)) {
    const label = serviceCodeValueOptions.find(
      (opt) => opt.value === rule.value
    )?.label;
    return label ?? rule.value;
  }
  if (isBusinessTypeRule(rule)) {
    const label = getBusinessTypeLabel(
      Number(rule.value),
      context?.businessTypeLabels ?? null
    );
    return label !== '-' ? label : rule.value;
  }
  return rule.value;
}

export const orderAutoMapDeviceTypeOptions = [2, 3, 11].map((value) => ({
  value,
  label: mccsDeviceType[value]
}));

export function getDeviceTypeDisplayName(deviceType: number | null) {
  if (deviceType == null) return '';
  return mccsDeviceType[deviceType] ?? String(deviceType);
}

export function enrichOrderAutoMapDisplayNames(
  row: OrderAutoMapRow
): OrderAutoMapRow {
  return {
    ...row,
    deviceTypeName: getDeviceTypeDisplayName(row.deviceType),
    productName:
      row.productName ?? (row.productId != null ? String(row.productId) : '')
  };
}

/** camelCase key: lowercase start, then letters/digits (e.g. businessType, isHarnessCut). */
export const CONDITION_FIELD_NAME_PATTERN = /^[a-z][a-zA-Z0-9]*$/;

export function isValidConditionFieldName(fieldName: string): boolean {
  return CONDITION_FIELD_NAME_PATTERN.test(fieldName);
}

export const orderAutoMapFieldOptions = [
  { label: BUSINESS_TYPE_FIELD_LABEL, value: BUSINESS_TYPE_FIELD },
  { label: 'サービスコード', value: 'serviceCode' },
  { label: HARNESS_CUT_FIELD_LABEL, value: 'isHarnessCut' },
  { label: IGNITION_TYPE_FIELD_LABEL, value: 'ignitionType' },
  { label: MOUNTING_PRICE_FIELD_LABEL, value: MOUNTING_PRICE_FIELD },
  { label: REMOVAL_PRICE_FIELD_LABEL, value: REMOVAL_PRICE_FIELD }
];

const PRESET_FIELD_DEFAULT_TYPES: Record<string, ConditionValueType> = {
  [BUSINESS_TYPE_FIELD]: 'number',
  [SERVICE_CODE_FIELD]: 'string',
  [HARNESS_CUT_FIELD]: 'boolean',
  [IGNITION_TYPE_FIELD]: 'number',
  [MOUNTING_PRICE_FIELD]: 'number',
  [REMOVAL_PRICE_FIELD]: 'number'
};

export function isPresetOrderAutoMapField(fieldName: string): boolean {
  return fieldName in PRESET_FIELD_DEFAULT_TYPES;
}

export function getPresetFieldDefaultType(
  fieldName: string
): ConditionValueType | null {
  return PRESET_FIELD_DEFAULT_TYPES[fieldName] ?? null;
}

export function getConditionTypeLabel(type: ConditionValueType): string {
  return conditionTypeOptions.find((opt) => opt.value === type)?.label ?? type;
}

export function countConditionRules(group: ConditionGroup): number {
  return group.rules.length;
}

function getOperatorSymbol(operator: string) {
  const symbols: Record<string, string> = {
    equal: '=',
    not_equal: '!=',
    greater: '>',
    greater_equal: '>=',
    less: '<',
    less_equal: '<=',
    contains: '~',
    not_contains: '!~',
    starts_with: '^=',
    ends_with: '$='
  };
  return symbols[operator] ?? operator;
}

export type ConditionSummaryPart = {
  text: string;
  bold?: boolean;
  combinator?: boolean;
};

function formatRuleWithOperator(
  left: string,
  operator: string,
  right: string
): ConditionSummaryPart[] {
  return [
    { text: left },
    { text: ` ${operator} `, bold: true },
    { text: right }
  ];
}

export function formatConditionRuleParts(
  rule: ConditionRule,
  context?: ConditionDisplayContext
): ConditionSummaryPart[] {
  if (isHarnessCutRule(rule) && rule.type === 'boolean') {
    return [{ text: formatConditionValue(rule, context) }];
  }
  if (isIgnitionTypeRule(rule)) {
    return formatRuleWithOperator(
      IGNITION_TYPE_FIELD_LABEL,
      '=',
      formatConditionValue(rule, context)
    );
  }
  if (isServiceCodeRule(rule)) {
    return formatRuleWithOperator(
      'サービスコード',
      '=',
      formatConditionValue(rule, context)
    );
  }
  if (isBusinessTypeRule(rule)) {
    return formatRuleWithOperator(
      BUSINESS_TYPE_FIELD_LABEL,
      getOperatorSymbol(rule.condition),
      formatConditionValue(rule, context)
    );
  }
  return formatRuleWithOperator(
    getFieldDisplayLabel(rule.fieldName),
    getOperatorSymbol(rule.condition),
    formatConditionValue(rule, context)
  );
}

export function formatConditionRule(
  rule: ConditionRule,
  context?: ConditionDisplayContext
) {
  return formatConditionRuleParts(rule, context)
    .map((part) => part.text)
    .join('');
}

export function getConditionSummary(condition: string) {
  if (!condition) return '';
  try {
    const parsed = JSON.parse(condition) as ConditionGroup;
    if (!parsed.rules.length) return '';
    return parsed.rules.map((rule) => formatConditionRule(rule)).join(' AND ');
  } catch {
    return condition;
  }
}

export function getConditionSummaryParts(
  condition: string,
  context?: ConditionDisplayContext
): ConditionSummaryPart[] {
  if (!condition) return [];
  try {
    const parsed = JSON.parse(condition) as ConditionGroup;
    if (!parsed.rules.length) return [];
    const parts: ConditionSummaryPart[] = [];
    parsed.rules.forEach((rule, index) => {
      if (index > 0) {
        parts.push({
          text: 'AND',
          bold: true,
          combinator: true
        });
      }
      parts.push(...formatConditionRuleParts(rule, context));
    });
    return parts;
  } catch {
    return [{ text: condition }];
  }
}

export function parseConditionGroup(condition: string): ConditionGroup {
  if (!condition) {
    return { rules: [] };
  }
  try {
    const parsed = JSON.parse(condition) as { rules?: ConditionRule[] };
    return { rules: parsed.rules ?? [] };
  } catch {
    return { rules: [] };
  }
}
